import React, { useState, useEffect } from 'react';
import { db, storage } from '../../../firebase';
import { collection, onSnapshot, query, orderBy, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref as sRef, deleteObject } from 'firebase/storage';
import { 
    Users, 
    Trash2, 
    Edit2, 
    Loader2, 
    UserPlus, 
    Youtube, 
    User, 
    Eye, 
    EyeOff, 
    FileText, 
    CheckCircle2, 
    Clock, 
    ExternalLink, 
    Mail, 
    PhoneCall, 
    Sparkles, 
    UserCheck,
    Check,
    XCircle
} from 'lucide-react';
import { SubjectType, TeacherBook, TeacherItem, TeacherApplicationModel } from '../../../models';
import TeacherForm from './TeacherForm';

const TeachersRoot: React.FC = () => {
    // Navigation Sub Tab: 'teachers' (등록된 강사 프로필) | 'applications' (강사 채용 지원자)
    const [activeSubTab, setActiveSubTab] = useState<'teachers' | 'applications'>('teachers');

    // Teachers List State
    const [teachers, setTeachers] = useState<TeacherItem[]>([]);
    const [loading, setLoading] = useState(true);

    // Teacher Applications State
    const [applications, setApplications] = useState<TeacherApplicationModel[]>([]);
    const [appsLoading, setAppsLoading] = useState(true);
    const [appStatusFilter, setAppStatusFilter] = useState<'all' | 'pending' | 'reviewed' | 'contacted' | 'rejected'>('all');
    
    // Modal & Form State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTeacher, setEditingTeacher] = useState<TeacherItem | null>(null);

    // Fetch Teachers Realtime
    useEffect(() => {
        const q = query(collection(db, 'teachers'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: TeacherItem[] = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() } as TeacherItem);
            });
            setTeachers(list);
            setLoading(false);
        }, (err) => {
            console.error('Error fetching teachers:', err);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Fetch Teacher Applications Realtime
    useEffect(() => {
        const q = query(collection(db, 'teacherApplications'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: TeacherApplicationModel[] = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() } as TeacherApplicationModel);
            });
            setApplications(list);
            setAppsLoading(false);
        }, (err) => {
            console.error('Error fetching teacher applications:', err);
            setAppsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleOpenAddModal = () => {
        setEditingTeacher(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (teacher: TeacherItem) => {
        setEditingTeacher(teacher);
        setIsModalOpen(true);
    };

    const handleDelete = async (teacher: TeacherItem) => {
        if (!confirm(`${teacher.name} 강사 정보를 삭제하시겠습니까?`)) return;

        try {
            await deleteDoc(doc(db, 'teachers', teacher.id));
            if (teacher.imageUrl) {
                try {
                    const storageRef = sRef(storage, teacher.imageUrl);
                    await deleteObject(storageRef);
                } catch (err) {
                    console.warn('Failed to delete profile image:', err);
                }
            }
            if (teacher.booksList && teacher.booksList.length > 0) {
                for (const book of teacher.booksList) {
                    if (book.imageUrl) {
                        try {
                            const storageRef = sRef(storage, book.imageUrl);
                            await deleteObject(storageRef);
                        } catch (err) {
                            console.warn('Failed to delete book image:', err);
                        }
                    }
                }
            }
            alert('강사 정보가 삭제되었습니다.');
        } catch (error) {
            console.error('Error deleting teacher:', error);
            alert('삭제 처리 중 오류가 발생했습니다.');
        }
    };

    const handleToggleHideMain = async (teacher: TeacherItem) => {
        try {
            const newStatus = !teacher.hideMainPhoto;
            await updateDoc(doc(db, 'teachers', teacher.id), {
                hideMainPhoto: newStatus
            });
        } catch (error) {
            console.error('Error toggling main photo visibility:', error);
            alert('설정 변경 중 오류가 발생했습니다.');
        }
    };

    const handleToggleHideIntro = async (teacher: TeacherItem) => {
        try {
            const newStatus = !teacher.hideIntroPhoto;
            await updateDoc(doc(db, 'teachers', teacher.id), {
                hideIntroPhoto: newStatus
            });
        } catch (error) {
            console.error('Error toggling intro photo visibility:', error);
            alert('설정 변경 중 오류가 발생했습니다.');
        }
    };

    // Applications Actions
    const handleUpdateAppStatus = async (appId: string, newStatus: TeacherApplicationModel['status']) => {
        try {
            await updateDoc(doc(db, 'teacherApplications', appId), {
                status: newStatus
            });
        } catch (err) {
            console.error('Error updating application status:', err);
            alert('상태 변경 중 오류가 발생했습니다.');
        }
    };

    const handleDeleteApp = async (app: TeacherApplicationModel) => {
        if (!confirm(`${app.name} 지원자의 지원서를 삭제하시겠습니까?`)) return;
        try {
            await deleteDoc(doc(db, 'teacherApplications', app.id));
            alert('지원서가 삭제되었습니다.');
        } catch (err) {
            console.error('Error deleting application:', err);
            alert('삭제 중 오류가 발생했습니다.');
        }
    };

    const handleConvertAppToTeacher = (app: TeacherApplicationModel) => {
        setEditingTeacher({
            id: '',
            name: app.name,
            subject: app.subject,
            career: app.career,
            intro: app.intro,
            imageUrl: app.imageUrl || '',
            youtubeUrl: '',
            booksList: [],
            createdAt: Date.now()
        });
        setIsModalOpen(true);
    };

    // Counts for Applications
    const pendingAppsCount = applications.filter(a => a.status === 'pending').length;

    // Filtered Applications
    const filteredApps = appStatusFilter === 'all' 
        ? applications 
        : applications.filter(a => a.status === appStatusFilter);

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b border-slate-100 px-8 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0 shadow-xs">
                <div className="space-y-1 text-left">
                    <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
                        <Users className="text-indigo-600" size={24} />
                        강사 프로필 및 채용 관리
                    </h1>
                    <p className="text-slate-500 font-medium text-xs md:text-sm">
                        웹사이트에 노출되는 대표 강사진 프로필과 풋터에서 접수된 강사 지원 현황을 관리합니다.
                    </p>
                </div>

                {/* Sub Tab Buttons */}
                <div className="flex items-center gap-2 bg-slate-100/80 p-1.5 rounded-2xl border border-slate-200/60 self-start md:self-auto">
                    <button
                        type="button"
                        onClick={() => setActiveSubTab('teachers')}
                        className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer ${
                            activeSubTab === 'teachers'
                                ? 'bg-white text-indigo-600 shadow-sm'
                                : 'text-slate-500 hover:text-slate-800'
                        }`}
                    >
                        <Users size={14} />
                        <span>강사 프로필 ({teachers.length})</span>
                    </button>
                    
                    <button
                        type="button"
                        onClick={() => setActiveSubTab('applications')}
                        className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer relative ${
                            activeSubTab === 'applications'
                                ? 'bg-white text-indigo-600 shadow-sm'
                                : 'text-slate-500 hover:text-slate-800'
                        }`}
                    >
                        <FileText size={14} />
                        <span>강사 지원자 ({applications.length})</span>
                        {pendingAppsCount > 0 && (
                            <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-black flex items-center justify-center animate-pulse">
                                {pendingAppsCount}
                            </span>
                        )}
                    </button>
                </div>

                {activeSubTab === 'teachers' && (
                    <button
                        type="button"
                        onClick={handleOpenAddModal}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-5 py-3 font-black text-sm flex items-center gap-2 transition-all shadow-lg shadow-indigo-100 active:scale-95 cursor-pointer self-start md:self-auto"
                    >
                        <UserPlus size={16} />
                        새 강사 등록
                    </button>
                )}
            </div>

            {/* Content Body */}
            <div className="flex-1 p-8">
                {activeSubTab === 'teachers' ? (
                    /* TAB 1: Registered Teachers List */
                    loading ? (
                        <div className="bg-white rounded-[2rem] border border-slate-100 py-32 flex flex-col items-center justify-center space-y-3 shadow-sm">
                            <Loader2 className="animate-spin text-indigo-600" size={32} />
                            <p className="text-slate-450 font-medium text-sm">강사 목록을 불러오는 중...</p>
                        </div>
                    ) : teachers.length === 0 ? (
                        <div className="bg-white rounded-[2rem] border border-slate-100 py-24 text-center text-slate-450 shadow-sm">
                            <Users size={48} className="mx-auto text-slate-300 mb-4" />
                            <p className="font-bold text-lg text-slate-700">등록된 강사가 없습니다.</p>
                            <p className="text-xs text-slate-400 font-medium mt-1">오른쪽 위의 '새 강사 등록' 버튼을 눌러 강사 프로필을 채워보세요.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {teachers.map((item) => (
                                <div
                                    key={item.id}
                                    className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden shadow-sm flex flex-col h-full group"
                                >
                                    <div className="aspect-[4/5] bg-slate-150 relative overflow-hidden shrink-0">
                                        <img
                                            src={item.imageUrl}
                                            alt={item.name}
                                            className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                                        />
                                        {/* Photo Hidden Badges */}
                                        {(item.hideMainPhoto || item.hideIntroPhoto) && (
                                            <div className="absolute top-3 left-3 flex flex-col gap-1 z-10">
                                                {item.hideMainPhoto && (
                                                    <span className="px-2 py-0.5 rounded-full bg-amber-500/90 text-white text-[9px] font-black shadow-xs backdrop-blur-xs flex items-center gap-1">
                                                        <EyeOff size={10} /> 메인 숨김
                                                    </span>
                                                )}
                                                {item.hideIntroPhoto && (
                                                    <span className="px-2 py-0.5 rounded-full bg-orange-500/90 text-white text-[9px] font-black shadow-xs backdrop-blur-xs flex items-center gap-1">
                                                        <EyeOff size={10} /> 소개 숨김
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Controls & Actions Bar BELOW Photo */}
                                    <div className="p-3 bg-slate-50 border-b border-slate-100 flex flex-col gap-2 shrink-0 text-left">
                                        {/* Row 1: YouTube Link + Edit/Delete Buttons */}
                                        <div className="flex items-center justify-between gap-2">
                                            {item.youtubeUrl ? (
                                                <a
                                                    href={item.youtubeUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-black shadow-xs transition-all active:scale-95 border border-red-500 cursor-pointer whitespace-nowrap"
                                                    title="유튜브 OT 영상 보기"
                                                >
                                                    <Youtube size={13} />
                                                    <span>OT 영상</span>
                                                </a>
                                            ) : (
                                                <span className="text-[10px] font-bold text-slate-400 whitespace-nowrap flex items-center gap-1">
                                                    <Youtube size={12} className="text-slate-350" /> 미등록
                                                </span>
                                            )}

                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenEditModal(item)}
                                                    className="w-8 h-8 rounded-xl bg-white hover:bg-slate-100 text-slate-700 flex items-center justify-center shadow-xs transition-all active:scale-95 border border-slate-200 cursor-pointer"
                                                    title="수정"
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(item)}
                                                    className="w-8 h-8 rounded-xl bg-white hover:bg-red-50 text-red-500 hover:text-red-650 flex items-center justify-center shadow-xs transition-all active:scale-95 border border-slate-200 cursor-pointer"
                                                    title="삭제"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Row 2: Photo Visibility Quick Toggles */}
                                        <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-slate-200/60">
                                            <button
                                                type="button"
                                                onClick={() => handleToggleHideMain(item)}
                                                className={`px-2 py-1.5 rounded-xl text-[10px] font-black border flex items-center justify-center gap-1 transition-all active:scale-95 cursor-pointer whitespace-nowrap ${
                                                    item.hideMainPhoto
                                                        ? 'bg-amber-100 text-amber-800 border-amber-300'
                                                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                                                }`}
                                                title={item.hideMainPhoto ? "메인 사진 가림 중 (클릭 시 사진 노출)" : "메인 사진 노출 중 (클릭 시 사진 가림)"}
                                            >
                                                {item.hideMainPhoto ? <EyeOff size={11} className="text-amber-700 shrink-0" /> : <Eye size={11} className="text-slate-400 shrink-0" />}
                                                <span>메인 {item.hideMainPhoto ? '가림' : '노출'}</span>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => handleToggleHideIntro(item)}
                                                className={`px-2 py-1.5 rounded-xl text-[10px] font-black border flex items-center justify-center gap-1 transition-all active:scale-95 cursor-pointer whitespace-nowrap ${
                                                    item.hideIntroPhoto
                                                        ? 'bg-amber-100 text-amber-800 border-amber-300'
                                                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                                                }`}
                                                title={item.hideIntroPhoto ? "강사소개 사진 가림 중 (클릭 시 사진 노출)" : "강사소개 사진 노출 중 (클릭 시 사진 가림)"}
                                            >
                                                {item.hideIntroPhoto ? <EyeOff size={11} className="text-amber-700 shrink-0" /> : <Eye size={11} className="text-slate-400 shrink-0" />}
                                                <span>소개 {item.hideIntroPhoto ? '가림' : '노출'}</span>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="p-6 flex-1 flex flex-col justify-between gap-4 text-left">
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {item.subject && (
                                                    <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-650 text-[10px] font-black border border-indigo-100">
                                                        {item.subject}
                                                    </span>
                                                )}
                                                <h4 className="font-black text-slate-800 text-lg">{item.name} 강사</h4>
                                            </div>
                                            
                                            {item.career && (
                                                <div className="space-y-1">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-0.5">주요 경력</p>
                                                    <p className="text-xs text-slate-600 font-semibold whitespace-pre-line leading-relaxed line-clamp-3">
                                                        {item.career}
                                                    </p>
                                                </div>
                                            )}

                                            {item.intro && (
                                                <div className="space-y-1 pt-1 border-t border-slate-100">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-0.5">소개글</p>
                                                    <p className="text-xs text-slate-550 font-medium leading-relaxed line-clamp-2">
                                                        {item.intro}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {item.booksList && item.booksList.length > 0 && (
                                            <div className="mt-2 pt-3 border-t border-slate-100 space-y-2">
                                                <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest pl-0.5">대표 교재 라인업 ({item.booksList.length})</p>
                                                <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                                                    {item.booksList.map((book) => (
                                                        <div key={book.id} className="flex gap-2.5 items-start bg-slate-50 p-1.5 rounded-lg border border-slate-100">
                                                            {book.imageUrl && (
                                                                <div className="w-8 h-10 rounded bg-white border border-slate-200 overflow-hidden shrink-0">
                                                                    <img src={book.imageUrl} alt={book.name} className="w-full h-full object-cover" />
                                                                </div>
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-bold text-slate-700 text-[11px] truncate">{book.name}</p>
                                                                {book.desc && (
                                                                    <p className="text-[9px] text-slate-400 whitespace-pre-line">{book.desc}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    /* TAB 2: Teacher Applications List */
                    <div className="space-y-6 text-left">
                        {/* Application Status Filter Chips */}
                        <div className="flex items-center gap-2 flex-wrap bg-white p-3 rounded-2xl border border-slate-100 shadow-xs">
                            <span className="text-xs font-black text-slate-400 uppercase tracking-wider pl-2 mr-2">상태 필터:</span>
                            {[
                                { id: 'all', label: '전체', count: applications.length },
                                { id: 'pending', label: '대기 중', count: applications.filter(a => a.status === 'pending').length },
                                { id: 'reviewed', label: '검토 완료', count: applications.filter(a => a.status === 'reviewed').length },
                                { id: 'contacted', label: '연락 완료', count: applications.filter(a => a.status === 'contacted').length },
                                { id: 'rejected', label: '보관', count: applications.filter(a => a.status === 'rejected').length }
                            ].map((chip) => (
                                <button
                                    key={chip.id}
                                    type="button"
                                    onClick={() => setAppStatusFilter(chip.id as any)}
                                    className={`px-3.5 py-1.5 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 cursor-pointer ${
                                        appStatusFilter === chip.id
                                            ? 'bg-indigo-600 text-white shadow-md shadow-indigo-100'
                                            : 'bg-slate-100/80 text-slate-600 hover:bg-slate-200/80'
                                    }`}
                                >
                                    <span>{chip.label}</span>
                                    <span className={`text-[10px] px-1.5 py-0.2 rounded-md ${
                                        appStatusFilter === chip.id ? 'bg-indigo-700/80 text-indigo-100' : 'bg-slate-200 text-slate-600'
                                    }`}>
                                        {chip.count}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {appsLoading ? (
                            <div className="bg-white rounded-[2rem] border border-slate-100 py-32 flex flex-col items-center justify-center space-y-3 shadow-sm">
                                <Loader2 className="animate-spin text-indigo-600" size={32} />
                                <p className="text-slate-450 font-medium text-sm">강사 지원자 목록을 불러오는 중...</p>
                            </div>
                        ) : filteredApps.length === 0 ? (
                            <div className="bg-white rounded-[2rem] border border-slate-100 py-24 text-center text-slate-450 shadow-sm space-y-2">
                                <FileText size={48} className="mx-auto text-slate-300 mb-2" />
                                <p className="font-bold text-lg text-slate-700">접수된 강사 지원서가 없습니다.</p>
                                <p className="text-xs text-slate-400 font-medium">웹사이트 풋터의 '강사 지원하기' 양식을 통해 제출된 지원서가 이곳에 표시됩니다.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {filteredApps.map((app) => (
                                    <div
                                        key={app.id}
                                        className="bg-white rounded-[2rem] border border-slate-150 overflow-hidden shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 border-l-4 border-l-indigo-600"
                                    >
                                        <div className="p-6 space-y-5">
                                            {/* Header row: Name, Subject, Date, Status */}
                                            <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-14 h-16 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center shadow-xs">
                                                        {app.imageUrl ? (
                                                            <img src={app.imageUrl} alt={app.name} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 bg-slate-100">
                                                                <User size={22} />
                                                                <span className="text-[8px] font-bold mt-0.5">미등록</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="px-2.5 py-0.5 rounded-md bg-indigo-50 text-indigo-650 text-xs font-black border border-indigo-100">
                                                                {app.subject}
                                                            </span>
                                                            <h3 className="text-lg font-black text-slate-900">{app.name} 지원자</h3>
                                                        </div>
                                                        <p className="text-[11px] text-slate-400 font-bold flex items-center gap-1">
                                                            <Clock size={12} className="text-slate-400" />
                                                            {new Date(app.createdAt).toLocaleString('ko-KR', {
                                                                year: 'numeric',
                                                                month: '2-digit',
                                                                day: '2-digit',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Status Badge */}
                                                <div>
                                                    {app.status === 'pending' && (
                                                        <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-black border border-amber-200 flex items-center gap-1">
                                                            <Clock size={12} /> 대기 중
                                                        </span>
                                                    )}
                                                    {app.status === 'reviewed' && (
                                                        <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-black border border-blue-200 flex items-center gap-1">
                                                            <FileText size={12} /> 검토 완료
                                                        </span>
                                                    )}
                                                    {app.status === 'contacted' && (
                                                        <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-black border border-emerald-200 flex items-center gap-1">
                                                            <CheckCircle2 size={12} /> 연락 완료
                                                        </span>
                                                    )}
                                                    {app.status === 'rejected' && (
                                                        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-black border border-slate-200 flex items-center gap-1">
                                                            <XCircle size={12} /> 보관
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Contact Info Pills */}
                                            <div className="flex flex-wrap items-center gap-3 text-xs font-bold text-slate-600">
                                                <a
                                                    href={`tel:${app.phone}`}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-50 hover:bg-indigo-50 hover:text-indigo-600 transition-colors border border-slate-200"
                                                >
                                                    <PhoneCall size={13} className="text-slate-400" />
                                                    <span>{app.phone}</span>
                                                </a>
                                                {app.email && (
                                                    <a
                                                        href={`mailto:${app.email}`}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-50 hover:bg-indigo-50 hover:text-indigo-600 transition-colors border border-slate-200"
                                                    >
                                                        <Mail size={13} className="text-slate-400" />
                                                        <span>{app.email}</span>
                                                    </a>
                                                )}
                                                {app.portfolioUrl && (
                                                    <a
                                                        href={app.portfolioUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 text-indigo-650 hover:bg-indigo-100 transition-colors border border-indigo-200"
                                                    >
                                                        <ExternalLink size={13} />
                                                        <span>포트폴리오/이력서 URL</span>
                                                    </a>
                                                )}
                                            </div>

                                            {/* Career & Intro Details */}
                                            <div className="space-y-3 bg-slate-50/70 p-4 rounded-2xl border border-slate-100">
                                                <div className="space-y-1">
                                                    <p className="text-[10px] font-black text-slate-450 uppercase tracking-widest">주요 학력 및 약력 / 출강 경력</p>
                                                    <p className="text-xs text-slate-700 font-semibold leading-relaxed whitespace-pre-line">
                                                        {app.career}
                                                    </p>
                                                </div>
                                                <div className="space-y-1 pt-2 border-t border-slate-200/50">
                                                    <p className="text-[10px] font-black text-slate-455 uppercase tracking-widest">자기소개 및 지도 철학</p>
                                                    <p className="text-xs text-slate-600 font-medium leading-relaxed whitespace-pre-line">
                                                        {app.intro}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Applicant Card Toolbar */}
                                        <div className="bg-slate-50 px-6 py-3 border-t border-slate-150 flex flex-wrap items-center justify-between gap-3">
                                            {/* Status Change Selector */}
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-[11px] font-black text-slate-400 mr-1">상태변경:</span>
                                                {(['pending', 'reviewed', 'contacted', 'rejected'] as const).map((st) => (
                                                    <button
                                                        key={st}
                                                        type="button"
                                                        onClick={() => handleUpdateAppStatus(app.id, st)}
                                                        className={`px-2.5 py-1 rounded-lg text-[10px] font-black border transition-all cursor-pointer ${
                                                            app.status === st
                                                                ? 'bg-slate-800 text-white border-slate-900 shadow-xs'
                                                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                                                        }`}
                                                    >
                                                        {st === 'pending' && '대기'}
                                                        {st === 'reviewed' && '검토완료'}
                                                        {st === 'contacted' && '연락완료'}
                                                        {st === 'rejected' && '보관'}
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Action Buttons: Convert to Teacher Profile & Delete */}
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleConvertAppToTeacher(app)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black shadow-xs transition-all active:scale-95 cursor-pointer"
                                                    title="이 지원자 정보로 새 강사 프로필 추가 등록"
                                                >
                                                    <UserCheck size={14} />
                                                    <span>강사로 등록</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteApp(app)}
                                                    className="w-8 h-8 rounded-xl bg-white hover:bg-red-50 text-red-500 hover:text-red-650 flex items-center justify-center shadow-xs transition-all active:scale-95 border border-slate-200 cursor-pointer"
                                                    title="지원서 삭제"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Save Modal */}
            {isModalOpen && (
                <TeacherForm
                    teacher={editingTeacher}
                    onClose={() => setIsModalOpen(false)}
                    onSave={() => setIsModalOpen(false)}
                />
            )}
        </div>
    );
};

export default TeachersRoot;
